import { useState, ChangeEvent, useEffect } from 'react'
import { useDebounce } from '../../../hooks/ds/useDebounce'
import { toast } from '@wiggui/ds/Toast'
import { usePropertiesRules } from '../../../../auth/hooks/usePropertiesRules'
import { getContactByName, shareProperties } from '../../../services'
import { useModalShareFileStore } from '../../../store'
import type { IContactData } from '../../../../interfaces'
import { EMAIL_REGEX } from '../../../constants'
import { useMatchWindowMediaQuery } from '../../../utils'

const errorMessageShare = {
  'There is another contact with the same data':
    'Ya existe un contacto con el mismo email y/o teléfono',
  'Property not found': 'No se encontró la propiedad que deseas compartir'
}

const handleErrorMessageShare = (error: string) => {
  const errorMessage =
    errorMessageShare[error as keyof typeof errorMessageShare]

  return errorMessage ?? 'Ocurrio un error. No se pudo compartir la ficha'
}

export const useShareFileProperty = () => {
  const { propertiesRules } = usePropertiesRules()
  const { isMD } = useMatchWindowMediaQuery()
  const [isLoading, setIsLoading] = useState(false)

  const [dataCustomer, setDataCustomer] = useState({
    contactId: '',
    name: '',
    email: '',
    phone: '',
    phoneCode: '+52'
  })

  const [isLoadingSearch, setIsLoadingSearch] = useState(false)
  const [searchValueCustomer, setSearchValueCustomer] = useState<string>('')
  const [emailIsInvalid, setEmailIsInvalid] = useState(false)
  const [resultSearchCustomer, setResultSearchCustomer] = useState<
    IContactData[]
  >([])
  const [selectedCustomer, setSelectedCustomer] = useState<IContactData | null>(
    null
  )

  const [isNewCustomer, setIsNewCustomer] = useState(false)

  const debounceSearchValueCustomer = useDebounce(searchValueCustomer, 750)

  const modalShareFile = useModalShareFileStore((state) => state.modalShareFile)

  const reset = useModalShareFileStore((state) => state.reset)

  const onClose = () => {
    reset()
    setDataCustomer({
      contactId: '',
      name: '',
      email: '',
      phone: '',
      phoneCode: ''
    })
    setSearchValueCustomer('')
  }

  const handleOnChange = (event: ChangeEvent) => {
    const target = event?.target as HTMLInputElement
    setDataCustomer({ ...dataCustomer, [target.name]: target?.value })
    if (target.name === 'email') {
      validateEmail(target.value)
    }
  }

  const handleOnChangePhoneCode = (phoneCode: string) => {
    setDataCustomer({ ...dataCustomer, phoneCode })
  }

  const fetchContactDataByName = async (name: string) => {
    setIsLoadingSearch(true)
    try {
      const { isSuccess, data } = await getContactByName({ name })
      if (isSuccess && data) {
        setResultSearchCustomer(data)
      }
    } finally {
      setIsLoadingSearch(false)
    }
  }

  const handleSelectContact = (contact: IContactData) => {
    setEmailIsInvalid(false)
    const phoneCode = contact?.mainPhone?.substring(0, 2) ?? ''
    const phone = contact?.mainPhone?.substring(2) ?? ''

    setDataCustomer({
      ...dataCustomer,
      contactId: contact?.contactId.toString(),
      name: contact?.name,
      email: contact?.mainEmail ?? '',
      phone,
      phoneCode
    })
    setSelectedCustomer({
      contactId: contact?.contactId,
      name: contact?.name,
      mainEmail: contact?.mainEmail,
      mainPhone: contact?.mainPhone
    })
    setSearchValueCustomer('')
  }

  const handleResetContact = () => {
    setDataCustomer({
      contactId: '',
      name: '',
      email: '',
      phone: '',
      phoneCode: ''
    })
    setSelectedCustomer(null)
  }

  const handleNewCustomer = () => {
    setIsNewCustomer(true)
    setDataCustomer({
      ...dataCustomer,
      name: searchValueCustomer
    })
  }

  const validateEmail = (email: string) => {
    const emailIsValid = Boolean(EMAIL_REGEX.test(email))
    setEmailIsInvalid(!emailIsValid)
  }

  const handleOnClick = async () => {
    try {
      setIsLoading(true)
      const formatPhoneCode = dataCustomer?.phoneCode?.replace('+', '')
      const { isSuccess, data, error } = await shareProperties({
        email:
          dataCustomer?.email?.length > 0 ? dataCustomer?.email : undefined,
        name: dataCustomer?.name,
        phone:
          formatPhoneCode && dataCustomer?.phone
            ? `${formatPhoneCode}${dataCustomer?.phone}`
            : '',
        propertyId: modalShareFile?.propertyId,
        sendEmail: Boolean(dataCustomer?.email?.length > 0),
        requirementId: undefined,
        contactId: dataCustomer?.contactId
          ? Number(dataCustomer?.contactId)
          : undefined
      })
      if (isSuccess) {
        toast({
          type: 'success',
          title: 'La ficha se compartió con éxito'
        })

        const baseWhatsappUrl = isMD
          ? 'https://web.whatsapp.com/send'
          : 'whatsapp://send'

        window.open(
          `${baseWhatsappUrl}?phone=${formatPhoneCode}${dataCustomer?.phone}&text=https://wiggot.com/datasheet/${data?.datasheetId}`,
          '_blank'
        )

        onClose()
        return
      }
      toast({
        type: 'error',
        title: handleErrorMessageShare(error?.data?.error?.message ?? '')
      })
    } catch (error: any) {
      toast({
        type: 'error',
        title: handleErrorMessageShare(error?.data?.error?.message ?? '')
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (debounceSearchValueCustomer?.length > 0) {
      fetchContactDataByName(debounceSearchValueCustomer)
    }
  }, [debounceSearchValueCustomer])

  const formIsDisabled = Boolean(
    propertiesRules?.CUSTOM_TECHNICAL_DATASHEET_EDIT_SHARE &&
      !propertiesRules.CUSTOM_TECHNICAL_DATASHEET_EDIT_SHARE?.value
  )

  return {
    dataCustomer,
    modalShareFile,
    handleSelectContact,
    handleOnClick,
    handleOnChangePhoneCode,
    isLoading,
    onClose,
    handleOnChange,
    resultSearchCustomer,
    isLoadingSearch,
    searchValueCustomer,
    setSearchValueCustomer,
    isNewCustomer,
    selectedCustomer,
    setIsNewCustomer,
    setSelectedCustomer,
    handleResetContact,
    handleNewCustomer,
    debounceSearchValueCustomer,
    formIsDisabled,
    validateEmail,
    emailIsInvalid
  }
}
